<script setup lang="ts">
import { mdiCheckCircle } from '@mdi/js'
import type { SceneInfo } from '~/types/interfaces'

// #719/#721 観点B: モバイルのリスト型シーンナビ（画像付き横型カード）。
// デスクトップの SVG マップ導線をスマホで置換する。表示のみを担い、遷移の実処理
// （CLICK イベント発火・音・ライフサイクル）は親（SceneMap）が onClickScene で行う。
//
// 画像はエリア=EmporiumIcon/AreaMap、ワンダー=デモ動画サムネ(useSceneImage)。
// 状態（クリア済み / キラキラ活性度）は useSceneAccess / props.sparkles で引く。
const props = defineProps<{
  items: SceneInfo[]
  /** 各シーンの攻略活性度（キラキラ）。world など一部画面のみ渡す。 */
  sparkles?: Record<string, number>
}>()

const emit = defineEmits<{ clickScene: [sceneId: string] }>()

const { isSceneCleared } = useSceneAccess()

// description は MDC/HTML（<br>・<ruby> 等）を含みうる。1〜2 行のプレビューなので
// まず <rt>（ふりがな）の中身を落としてから残りのタグを剥がし、素のテキストにする。
const plainDesc = (s: string): string =>
  s
    .replace(/<rt>.*?<\/rt>/g, '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const sparkleTier = (sceneId: string): number => props.sparkles?.[sceneId] ?? 0
</script>

<template>
  <nav class="scene-nav">
    <ul class="scene-nav__list">
      <li
        v-for="scene in items"
        :key="scene.sceneId"
        class="scene-nav__item"
      >
        <MobileListCard
          :image="sceneImageFor(scene)"
          :title="scene.name"
          :subtitle="scene.description ? plainDesc(scene.description) : undefined"
          image-fit="cover"
          @click="emit('clickScene', scene.sceneId)"
        >
          <template #meta>
            <!-- デスクトップのマップと同じく、クリア済み(✓)か未クリア(●)かだけを示し、
                 「ロック」の事前判定はしない。入場可否はタップ時にイベント系が案内する。 -->
            <span
              v-if="isSceneCleared(scene.sceneId)"
              class="scene-nav__badge scene-nav__badge--cleared"
            >
              <v-icon
                :icon="mdiCheckCircle"
                size="16"
              />
              クリア済み
            </span>
            <span
              v-else
              class="scene-nav__badge scene-nav__badge--open"
            >
              <span
                class="scene-nav__pulse"
                aria-hidden="true"
              />
              みどころ
            </span>

            <span
              v-if="sparkleTier(scene.sceneId)"
              class="scene-nav__sparkle"
              :aria-label="`活性度 ${sparkleTier(scene.sceneId)}`"
            >{{ '✨'.repeat(Math.min(sparkleTier(scene.sceneId), 3)) }}</span>
          </template>
        </MobileListCard>
      </li>
    </ul>

    <p
      v-if="items.length === 0"
      class="scene-nav__empty"
    >
      まだ行ける場所がありません。
    </p>
  </nav>
</template>

<style scoped>
.scene-nav {
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  padding: 12px 12px calc(12px + env(safe-area-inset-bottom));
  font-family: "Noto Sans JP", sans-serif;

  /* #721 2巡目: 縦センタリングは撤回。行き先が1件だと中央＝「下すぎ」て、タイトルと
     リストが離れて見えた。要望どおり「タイトルは上に少し余白／その下にリストを大きな
     余白なしで置く／下の余白は許容」の上詰めにする（justify-content は既定の flex-start）。 */
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.scene-nav__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.scene-nav__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.74rem;
  font-weight: 700;
  border-radius: 6px;
  padding: 2px 8px;
}

.scene-nav__badge--cleared {
  color: #2e7d54;
  background: rgba(75, 191, 129, 0.16);
}

.scene-nav__badge--open {
  color: #9a3320;
  background: rgba(177, 18, 38, 0.1);
}

.scene-nav__pulse {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #b11226;
  box-shadow: 0 0 0 0 rgba(245, 229, 170, 0.9);
  animation: scene-nav-pulse 2.2s ease-out infinite;
}

@keyframes scene-nav-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(177, 18, 38, 0.6);
  }

  100% {
    box-shadow: 0 0 0 8px rgba(177, 18, 38, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .scene-nav__pulse {
    animation: none;
  }
}

.scene-nav__sparkle {
  font-size: 0.8rem;
  letter-spacing: -2px;
  white-space: nowrap;
}

.scene-nav__empty {
  text-align: center;
  color: #ECEFF1;
  text-shadow: #000 1px 1px 3px;
  padding: 32px 12px;
  font-size: 0.95rem;
}
</style>
